import { useState } from "preact/hooks"
import { Link } from "wouter-preact"
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react"

import type { DetectedShortPayJson, ShortPayJson, ShortPayPatternJson, ShortPayStatus } from "@/api/accounting.tsx"
import {
  SortDir,
  SortValue,
  age_days,
  format_date,
  sort_rows,
  status_badge_class,
} from "@/accounting/short_pay_display.ts"
import type { ShortPayInvoiceRef } from "@/accounting/ShortPayInvoiceDialog.tsx"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { exactCurrencyFormatter } from "@/utils"
import { check_detail_path } from "./check_path"

/** Check number linked to its cash-application page, with the check date beside it. */
function CheckCell({ check_number, check_date }: { check_number?: string; check_date?: string }) {
  if (!check_number) {
    return <span class="text-slate-400">—</span>
  }
  return (
    <>
      <Link to={check_detail_path(check_number)} class="text-blue-600 hover:underline">
        {check_number}
      </Link>
      {check_date && <span class="text-xs text-slate-500 ml-2">{format_date(check_date)}</span>}
    </>
  )
}

/** Invoice number as a button opening the accounting-system detail (and PDF) for that invoice. */
function InvoiceCell({
  invoice,
  invoice_date,
  on_view_invoice,
}: {
  invoice: ShortPayInvoiceRef
  invoice_date?: string
  on_view_invoice: (invoice: ShortPayInvoiceRef) => void
}) {
  return (
    <>
      <button
        type="button"
        class="font-medium text-slate-900 underline decoration-slate-300 decoration-dotted underline-offset-4 hover:text-blue-700 hover:decoration-blue-400"
        title="View this invoice as the accounting system has it"
        onClick={() => on_view_invoice(invoice)}>
        {invoice.invoice_number}
      </button>
      {invoice_date && <span class="text-xs text-slate-500 ml-2">{format_date(invoice_date)}</span>}
    </>
  )
}

interface SortState<K extends string> {
  key: K
  dir: SortDir
}

/** One sortable column: its header label, alignment, first-click direction, and cell accessor. */
interface ColumnDef<T, K extends string> {
  key: K
  label: string
  right?: boolean
  /** Direction a fresh click sorts in — amounts read best biggest-first, text A-first. */
  first_dir: SortDir
  value: (row: T) => SortValue
}

function toggle_sort<K extends string>(current: SortState<K>, column: ColumnDef<never, K>): SortState<K> {
  if (current.key === column.key) {
    return { key: column.key, dir: current.dir === "asc" ? "desc" : "asc" }
  }
  return { key: column.key, dir: column.first_dir }
}

/** Column-header sort button: label plus the active direction (or a neutral hint on hover). */
function SortableHead<T, K extends string>({
  column,
  sort,
  on_sort,
}: {
  column: ColumnDef<T, K>
  sort: SortState<K>
  on_sort: (column: ColumnDef<T, K>) => void
}) {
  const active = sort.key === column.key
  return (
    <TableHead class="py-3" aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
      <button
        type="button"
        class={`group inline-flex items-center gap-1 whitespace-nowrap ${column.right ? "w-full justify-end" : ""} ${
          active ? "text-slate-900" : "hover:text-slate-700"
        }`}
        onClick={() => on_sort(column)}>
        {column.label}
        {active ? (
          sort.dir === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
        )}
      </button>
    </TableHead>
  )
}

type RecordedSortKey = "invoice" | "customer" | "check" | "total" | "short" | "age" | "status"

const RECORDED_COLUMNS: ColumnDef<ShortPayJson, RecordedSortKey>[] = [
  { key: "invoice", label: "Invoice #", first_dir: "asc", value: (row: ShortPayJson) => row.invoice_number },
  { key: "customer", label: "Customer", first_dir: "asc", value: (row: ShortPayJson) => row.customer_name },
  { key: "check", label: "Check", first_dir: "asc", value: (row: ShortPayJson) => row.check_number || undefined },
  { key: "total", label: "Invoice total", right: true, first_dir: "desc", value: (row: ShortPayJson) => Number(row.invoice_total) },
  { key: "short", label: "Short paid", right: true, first_dir: "desc", value: (row: ShortPayJson) => Number(row.amount) },
  { key: "age", label: "Age", right: true, first_dir: "desc", value: (row: ShortPayJson) => age_days(row.created_at) },
  { key: "status", label: "Status", first_dir: "asc", value: (row: ShortPayJson) => row.status },
]

interface RecordedShortPaysTableProps {
  rows: ShortPayJson[]
  saving_id: string | null
  on_set_status: (row: ShortPayJson, status: ShortPayStatus) => void
  /** Open the review-and-send dialog for chasing this money with the distributor. */
  on_file_dispute: (row: ShortPayJson) => void
  /** Open the accounting-system invoice detail (fields + PDF) for a row. */
  on_view_invoice: (invoice: ShortPayInvoiceRef) => void
}

/** Short pays already tracked in MarginWiz: flagged at apply time, or recorded from the detected list. */
export function RecordedShortPaysTable({
  rows,
  saving_id,
  on_set_status,
  on_file_dispute,
  on_view_invoice,
}: RecordedShortPaysTableProps) {
  const [sort, setSort] = useState<SortState<RecordedSortKey>>({ key: "age", dir: "asc" })
  const column_by_key = new Map(RECORDED_COLUMNS.map((c: ColumnDef<ShortPayJson, RecordedSortKey>) => [c.key, c]))
  const sorted = sort_rows(rows, sort.dir, column_by_key.get(sort.key)!.value)

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {RECORDED_COLUMNS.map((column: ColumnDef<ShortPayJson, RecordedSortKey>) => (
            <SortableHead
              key={column.key}
              column={column}
              sort={sort}
              on_sort={(c: ColumnDef<ShortPayJson, RecordedSortKey>) => setSort(toggle_sort(sort, c))}
            />
          ))}
          <TableHead class="py-3" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((row: ShortPayJson) => (
          <TableRow key={row.id}>
            <TableCell class="px-2 py-3">
              <InvoiceCell invoice={row} on_view_invoice={on_view_invoice} />
            </TableCell>
            <TableCell class="px-2 py-3 text-slate-700">{row.customer_name}</TableCell>
            <TableCell class="px-2 py-3">
              <CheckCell check_number={row.check_number} check_date={row.check_date} />
            </TableCell>
            <TableCell class="px-2 py-3 text-right tabular-nums">{exactCurrencyFormatter(Number(row.invoice_total))}</TableCell>
            <TableCell class="px-2 py-3 text-right tabular-nums font-medium text-amber-700">
              {exactCurrencyFormatter(Number(row.amount))}
            </TableCell>
            <TableCell class="px-2 py-3 text-right tabular-nums">
              <span class={age_days(row.created_at) > 30 ? "text-red-600 font-semibold" : "text-slate-700"}>
                {age_days(row.created_at)}d
              </span>
            </TableCell>
            <TableCell class="px-2 py-3">
              <span
                class={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${status_badge_class(row.status)}`}
                title={row.resolved_note || undefined}>
                {row.status}
              </span>
              {row.dispute_id && (
                <span class="ml-1.5 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  disputed
                </span>
              )}
            </TableCell>
            <TableCell class="px-2 py-3">
              <div class="flex justify-end gap-1.5">
                {row.status === "open" ? (
                  <>
                    {!row.dispute_id && (
                      <Button
                        size="sm"
                        class="h-7 text-xs"
                        disabled={saving_id === row.id}
                        onClick={() => on_file_dispute(row)}>
                        File dispute
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      class="h-7 text-xs"
                      disabled={saving_id === row.id}
                      onClick={() => on_set_status(row, "resolved")}>
                      Resolve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      class="h-7 text-xs"
                      disabled={saving_id === row.id}
                      onClick={() => on_set_status(row, "ignored")}>
                      Ignore
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-7 text-xs"
                    disabled={saving_id === row.id}
                    onClick={() => on_set_status(row, "open")}>
                    Reopen
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export function detected_row_key(row: DetectedShortPayJson): string {
  return `${row.accounting_invoice_id}:${row.deduction_id}`
}

/**
 * Customers shorting a consistent percentage across several invoices. That shape is a contractual
 * fee or off-invoice allowance rather than a one-off underpayment, so it's worth negotiating once
 * instead of chasing invoice by invoice. Clicking one filters the detected list to its invoices.
 */
export function ShortPayPatterns({
  patterns,
  selected,
  on_select,
}: {
  patterns: ShortPayPatternJson[]
  /** The pattern currently filtering the detected table, if any. */
  selected: ShortPayPatternJson | null
  /** Toggle the detected-table filter to this pattern's invoices (null clears it). */
  on_select: (pattern: ShortPayPatternJson | null) => void
}) {
  return (
    <div class="space-y-2">
      {patterns.map((pattern: ShortPayPatternJson) => {
        const is_selected =
          selected !== null && selected.customer_name === pattern.customer_name && selected.percent === pattern.percent
        return (
          <button
            key={`${pattern.customer_name}:${pattern.percent}`}
            type="button"
            class={`flex w-full flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${
              is_selected
                ? "border-blue-300 bg-blue-50/70 ring-1 ring-blue-200"
                : "border-slate-200/80 bg-slate-50/60 hover:border-slate-300 hover:bg-slate-100/70"
            }`}
            title={is_selected ? "Clear this filter" : "Show these invoices in the detected list below"}
            onClick={() => on_select(is_selected ? null : pattern)}>
            <span class="font-medium text-slate-900">{pattern.customer_name}</span>
            <span class="text-slate-600">
              is paying <span class="font-medium text-amber-700">{pattern.percent.toFixed(2)}%</span> under invoice on{" "}
              {pattern.invoice_count} invoices
            </span>
            <span class="ml-auto tabular-nums text-slate-700">
              {exactCurrencyFormatter(Number(pattern.amount))}
              <span class="text-slate-400">
                {" · "}
                {exactCurrencyFormatter(Number(pattern.outstanding))} still due
              </span>
            </span>
            <span class={`text-xs ${is_selected ? "text-blue-700" : "text-blue-600"}`}>
              {is_selected ? "Clear filter" : "View invoices"}
            </span>
          </button>
        )
      })}
    </div>
  )
}

type DetectedSortKey = "invoice" | "customer" | "check" | "total" | "settled" | "short" | "due" | "matched"

/** The dollar amount a detected row's payments settle: cash + early-pay discount + linked deductions. */
export function detected_settled(row: DetectedShortPayJson): number {
  return Math.round((Number(row.paid) + Number(row.discount) + Number(row.allowance)) * 100) / 100
}

const DETECTED_COLUMNS: ColumnDef<DetectedShortPayJson, DetectedSortKey>[] = [
  { key: "invoice", label: "Invoice #", first_dir: "asc", value: (row: DetectedShortPayJson) => row.invoice_number },
  { key: "customer", label: "Customer", first_dir: "asc", value: (row: DetectedShortPayJson) => row.customer_name },
  { key: "check", label: "Check", first_dir: "asc", value: (row: DetectedShortPayJson) => row.check_number || undefined },
  {
    key: "total",
    label: "Invoice total",
    right: true,
    first_dir: "desc",
    value: (row: DetectedShortPayJson) => Number(row.invoice_total),
  },
  { key: "settled", label: "Settled", right: true, first_dir: "desc", value: detected_settled },
  { key: "short", label: "Short paid", right: true, first_dir: "desc", value: (row: DetectedShortPayJson) => Number(row.amount) },
  { key: "due", label: "Still due", right: true, first_dir: "desc", value: (row: DetectedShortPayJson) => Number(row.outstanding) },
  {
    key: "matched",
    label: "Matched on",
    first_dir: "asc",
    value: (row: DetectedShortPayJson) => (row.matched_by === "link" ? "Linked invoice" : "Invoice number"),
  },
]

interface DetectedShortPaysTableProps {
  rows: DetectedShortPayJson[]
  saving_key: string | null
  on_record: (row: DetectedShortPayJson, status: ShortPayStatus) => void
  /** Record the row as an open short pay and go straight into the dispute dialog. */
  on_dispute: (row: DetectedShortPayJson) => void
  /** Open the accounting-system invoice detail (fields + PDF) for a row. */
  on_view_invoice: (invoice: ShortPayInvoiceRef) => void
}

/**
 * Underpayments found by comparing each synced accounting invoice against the `kind = Invoice`
 * transactions that paid it. Disputing (or tracking) a row records it as a short pay, which moves
 * it into the tracked list.
 */
export function DetectedShortPaysTable({ rows, saving_key, on_record, on_dispute, on_view_invoice }: DetectedShortPaysTableProps) {
  const [sort, setSort] = useState<SortState<DetectedSortKey>>({ key: "due", dir: "desc" })
  const column_by_key = new Map(DETECTED_COLUMNS.map((c: ColumnDef<DetectedShortPayJson, DetectedSortKey>) => [c.key, c]))
  const sorted = sort_rows(rows, sort.dir, column_by_key.get(sort.key)!.value)

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {DETECTED_COLUMNS.map((column: ColumnDef<DetectedShortPayJson, DetectedSortKey>) => (
            <SortableHead
              key={column.key}
              column={column}
              sort={sort}
              on_sort={(c: ColumnDef<DetectedShortPayJson, DetectedSortKey>) => setSort(toggle_sort(sort, c))}
            />
          ))}
          <TableHead class="py-3" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((row: DetectedShortPayJson) => {
          const key = detected_row_key(row)
          // Nothing left on the invoice in the accounting system means the gap was already credited
          // there — still worth seeing (it's a fee the brand is eating), but not collectable.
          const collectable = Number(row.outstanding) > 0
          const breakdown = [
            `Paid ${exactCurrencyFormatter(Number(row.paid))}`,
            row.discount ? `early-pay discount ${exactCurrencyFormatter(Number(row.discount))}` : null,
            row.allowance ? `deductions on the invoice ${exactCurrencyFormatter(Number(row.allowance))}` : null,
            row.payment_count > 1 ? `across ${row.payment_count} payments` : null,
          ]
            .filter(Boolean)
            .join(" + ")
          return (
            <TableRow key={key}>
              <TableCell class="px-2 py-3">
                <InvoiceCell invoice={row} invoice_date={row.invoice_date} on_view_invoice={on_view_invoice} />
              </TableCell>
              <TableCell class="px-2 py-3 text-slate-700">{row.customer_name}</TableCell>
              <TableCell class="px-2 py-3">
                <CheckCell check_number={row.check_number} check_date={row.check_date} />
              </TableCell>
              <TableCell class="px-2 py-3 text-right tabular-nums">{exactCurrencyFormatter(Number(row.invoice_total))}</TableCell>
              <TableCell class="px-2 py-3 text-right tabular-nums text-slate-700" title={breakdown}>
                {exactCurrencyFormatter(detected_settled(row))}
              </TableCell>
              <TableCell class="px-2 py-3 text-right tabular-nums font-medium text-amber-700">
                {exactCurrencyFormatter(Number(row.amount))}
              </TableCell>
              <TableCell
                class={`px-2 py-3 text-right tabular-nums ${collectable ? "font-medium text-slate-900" : "text-slate-400"}`}
                title={
                  collectable
                    ? undefined
                    : "The accounting system shows no balance left on this invoice — the gap was already credited or written off there."
                }>
                {row.still_due === undefined ? "—" : exactCurrencyFormatter(Number(row.outstanding))}
              </TableCell>
              <TableCell class="px-2 py-3 text-xs text-slate-500">
                {row.matched_by === "link" ? "Linked invoice" : "Invoice number"}
              </TableCell>
              <TableCell class="px-2 py-3">
                <div class="flex justify-end gap-1.5">
                  <Button size="sm" class="h-7 text-xs" disabled={saving_key === key} onClick={() => on_dispute(row)}>
                    Dispute
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-7 text-xs"
                    disabled={saving_key === key}
                    onClick={() => on_record(row, "open")}>
                    Track
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="h-7 text-xs"
                    disabled={saving_key === key}
                    onClick={() => on_record(row, "ignored")}>
                    Ignore
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
